import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

// CheckoutPage is now in its own dedicated file: src/pages/CheckoutPage.jsx
// This stub prevents any stray import from breaking during transition.
export function CheckoutPage() {
  // Moved to src/pages/CheckoutPage.jsx
  return null;
}

/* ── PROFILE / DASHBOARD ─────────────────────────────────── */

const STATUS_CFG = {
  pending: { cls: 'status-transit', icon: 'fa-hourglass-half', label: 'Pending' },
  processing: { cls: 'status-transit', icon: 'fa-box', label: 'Processing' },
  shipped: { cls: 'status-transit', icon: 'fa-truck', label: 'Shipped' },
  delivered: { cls: 'status-delivered', icon: 'fa-circle-check', label: 'Delivered' },
  transit: { cls: 'status-transit', icon: 'fa-truck', label: 'In Transit' },
  cancelled: { cls: 'status-cancelled', icon: 'fa-times-circle', label: 'Cancelled' },
  refunded: { cls: 'status-cancelled', icon: 'fa-rotate-left', label: 'Refunded' },
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(amount) || 0);

const formatOrderDate = (dateValue) => {
  if (!dateValue) return '--';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: '2-digit' });
};

const normalizeOrderStatus = (statusValue) => {
  const normalized = String(statusValue || '').toLowerCase();
  if (normalized === 'in_transit') return 'shipped';
  return normalized || 'pending';
};

const getOrderSummary = (order) => {
  const items = Array.isArray(order?.items) ? order.items : [];
  const firstItem = items[0];
  const firstProduct = firstItem && typeof firstItem.product === 'object' ? firstItem.product : null;
  const firstName = firstProduct?.name || 'Product';
  const extraCount = Math.max(items.length - 1, 0);

  return {
    id: order?.orderId || '--',
    title: extraCount > 0 ? `${firstName} +${extraCount} more` : firstName,
    image: firstProduct?.image || firstProduct?.images?.front || '',
    date: formatOrderDate(order?.createdAt),
    status: normalizeOrderStatus(order?.orderStatus || order?.paymentStatus),
    amount: Number(order?.totalPrice) || 0,
    trackingNumber: order?.shippingTracker?.trackingNumber || '',
    trackingUrl: order?.shippingTracker?.trackingUrl || '',
  };
};

const EMPTY_ADDRESS_FORM = {
  label: 'Home',
  fullName: '',
  phone: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'India',
  isDefault: false,
};

const EMPTY_PASSWORD_FORM = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const buildSettingsForm = (profile) => ({
  name: profile?.name || '',
  email: profile?.email || '',
  phone: profile?.phone || '',
  marketingEmails: profile?.settings?.marketingEmails ?? true,
  orderUpdates: profile?.settings?.orderUpdates ?? true,
  wishlistAlerts: profile?.settings?.wishlistAlerts ?? true,
});

const buildAddressForm = (address) => ({
  ...EMPTY_ADDRESS_FORM,
  ...(address || {}),
});

const getProfileInitials = (name) =>
  String(name || 'Guest User')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || 'GU';

const getMemberSinceLabel = (createdAt) => {
  const date = createdAt ? new Date(createdAt) : null;
  const year = date && !Number.isNaN(date.getTime()) ? date.getFullYear() : 2023;
  return `Since ${year}`;
};

const getAddressLines = (address) =>
  [
    address?.line1,
    address?.line2,
    [address?.city, address?.state, address?.postalCode].filter(Boolean).join(', '),
    address?.country,
  ].filter(Boolean);

const isSessionInvalidError = (error) => {
  const message = error?.response?.data?.message || '';
  return error?.response?.status === 401 || message === 'User not found';
};

const DASHBOARD_NAV = [
  { id: 'overview', icon: 'fa-grid-2', label: 'Overview' },
  { id: 'orders', icon: 'fa-bag-shopping', label: 'My Orders' },
  { id: 'wishlist', icon: 'fa-heart', label: 'Wishlist' },
  { id: 'addresses', icon: 'fa-map-location-dot', label: 'Addresses' },
  { id: 'settings', icon: 'fa-gear', label: 'Settings' },
];

const getDashboardSection = (sectionValue) =>
  DASHBOARD_NAV.some((item) => item.id === sectionValue) ? sectionValue : 'overview';

function OrderImage({ src, alt, className }) {
  const [hasError, setHasError] = useState(false);

  if (!src || hasError) {
    const letter = (alt || 'P').trim().charAt(0).toUpperCase();
    return (
      <div className={`${className} order-item-thumb-fallback`} aria-label={alt}>
        {letter}
      </div>
    );
  }

  return <img src={src} alt={alt} className={className} onError={() => setHasError(true)} />;
}

export function DashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedSection = searchParams.get('section');
  const [activeSection, setActiveSection] = useState(() => getDashboardSection(requestedSection));
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState('');
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState('');
  const [wishlistMessage, setWishlistMessage] = useState({ type: '', text: '' });
  const [settingsForm, setSettingsForm] = useState(() => buildSettingsForm(null));
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState({ type: '', text: '' });
  const [passwordForm, setPasswordForm] = useState(EMPTY_PASSWORD_FORM);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });
  const [addressForm, setAddressForm] = useState(EMPTY_ADDRESS_FORM);
  const [editingAddressId, setEditingAddressId] = useState('');
  const [addressEditorOpen, setAddressEditorOpen] = useState(false);
  const [addressSaving, setAddressSaving] = useState(false);
  const [addressMessage, setAddressMessage] = useState({ type: '', text: '' });
  const navigate = useNavigate();
  const { wishlist, wishlistLoading, wishlistError, toggleWishlist } = useStore();
  const { user, isAuthenticated, updateUser, logout } = useAuth();

  useEffect(() => {
    const fetchOrders = async () => {
      if (!isAuthenticated) {
        setOrders([]);
        setOrdersLoading(false);
        setOrdersError('Please sign in to view your orders.');
        return;
      }

      setOrdersLoading(true);
      setOrdersError('');
      try {
        const response = await api.get('/orders');
        setOrders(Array.isArray(response.data?.orders) ? response.data.orders : []);
      } catch (error) {
        setOrders([]);
        setOrdersError(error.response?.data?.message || 'Unable to load your orders right now.');
      } finally {
        setOrdersLoading(false);
      }
    };

    fetchOrders();
  }, [isAuthenticated]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!isAuthenticated) {
        setProfile(null);
        setProfileLoading(false);
        setProfileError('Please sign in to manage your account details.');
        setSettingsForm(buildSettingsForm(user));
        return;
      }

      setProfileLoading(true);
      setProfileError('');
      try {
        const response = await api.get('/profile');
        const loadedUser = response.data?.user || null;
        setProfile(loadedUser);
        setSettingsForm(buildSettingsForm(loadedUser));
        updateUser(loadedUser);
      } catch (error) {
        if (isSessionInvalidError(error)) {
          handleSessionInvalid('Your session is no longer valid. Please sign in again.');
          return;
        }
        setProfile(null);
        setProfileError(error.response?.data?.message || 'Unable to load your account details right now.');
      } finally {
        setProfileLoading(false);
      }
    };

    fetchProfile();
  }, [isAuthenticated]);

  const orderCards = useMemo(() => orders.map((order) => getOrderSummary(order)), [orders]);
  const recentOrders = useMemo(() => orderCards.slice(0, 3), [orderCards]);
  const totalSpent = useMemo(
    () => orderCards.reduce((sum, order) => sum + order.amount, 0),
    [orderCards]
  );
  const activeProfile = profile || user;
  const addresses = useMemo(() => activeProfile?.addresses || [], [activeProfile]);

  const resetAddressEditor = () => {
    setEditingAddressId('');
    setAddressForm(EMPTY_ADDRESS_FORM);
    setAddressEditorOpen(false);
  };

  useEffect(() => {
    setActiveSection(getDashboardSection(requestedSection));
  }, [requestedSection]);

  const handleSessionInvalid = (fallbackMessage) => {
    logout();
    setProfile(null);
    setProfileError(fallbackMessage);
    setAddressMessage({ type: 'danger', text: fallbackMessage });
    setSettingsMessage({ type: 'danger', text: fallbackMessage });
    setPasswordMessage({ type: 'danger', text: fallbackMessage });
    navigate('/login');
  };

  const applyUpdatedUser = (nextUser) => {
    setProfile(nextUser);
    setSettingsForm(buildSettingsForm(nextUser));
    updateUser(nextUser);
  };

  const handleWishlistRemove = async (product) => {
    setWishlistMessage({ type: '', text: '' });
    const result = await toggleWishlist(product);
    if (result.action === 'error') {
      setWishlistMessage({ type: 'danger', text: result.error || 'Unable to update wishlist right now.' });
    }
  };

  const handleSettingsSubmit = async (event) => {
    event.preventDefault();
    setSettingsSaving(true);
    setSettingsMessage({ type: '', text: '' });

    try {
      const profileResponse = await api.put('/profile', {
        name: settingsForm.name.trim(),
        email: settingsForm.email.trim(),
        phone: settingsForm.phone.trim(),
      });

      const settingsResponse = await api.put('/profile/settings', {
        marketingEmails: settingsForm.marketingEmails,
        orderUpdates: settingsForm.orderUpdates,
        wishlistAlerts: settingsForm.wishlistAlerts,
      });

      applyUpdatedUser(settingsResponse.data?.user || profileResponse.data?.user || activeProfile);
      setSettingsMessage({ type: 'success', text: 'Account settings updated successfully.' });
    } catch (error) {
      if (isSessionInvalidError(error)) {
        handleSessionInvalid('Your session expired while saving settings. Please sign in again.');
        return;
      }
      setSettingsMessage({
        type: 'danger',
        text: error.response?.data?.message || 'Unable to save account settings right now.',
      });
    } finally {
      setSettingsSaving(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setPasswordSaving(true);
    setPasswordMessage({ type: '', text: '' });

    try {
      const response = await api.put('/profile/password', passwordForm);
      setPasswordForm(EMPTY_PASSWORD_FORM);
      setPasswordMessage({
        type: 'success',
        text: response.data?.message || 'Password updated successfully.',
      });
    } catch (error) {
      if (isSessionInvalidError(error)) {
        handleSessionInvalid('Your session expired while updating your password. Please sign in again.');
        return;
      }
      setPasswordMessage({
        type: 'danger',
        text: error.response?.data?.message || 'Unable to update your password right now.',
      });
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleAddressSubmit = async (event) => {
    event.preventDefault();
    setAddressSaving(true);
    setAddressMessage({ type: '', text: '' });

    try {
      const payload = {
        ...addressForm,
        label: addressForm.label.trim(),
        fullName: addressForm.fullName.trim(),
        phone: addressForm.phone.trim(),
        line1: addressForm.line1.trim(),
        line2: addressForm.line2.trim(),
        city: addressForm.city.trim(),
        state: addressForm.state.trim(),
        postalCode: addressForm.postalCode.trim(),
        country: addressForm.country.trim(),
      };

      const response = editingAddressId
        ? await api.put(`/profile/addresses/${editingAddressId}`, payload)
        : await api.post('/profile/addresses', payload);

      applyUpdatedUser(response.data?.user || activeProfile);
      setAddressMessage({
        type: 'success',
        text: response.data?.message || 'Address saved successfully.',
      });
      resetAddressEditor();
    } catch (error) {
      if (isSessionInvalidError(error)) {
        handleSessionInvalid('Your session expired while saving the address. Please sign in again.');
        return;
      }
      setAddressMessage({
        type: 'danger',
        text: error.response?.data?.message || 'Unable to save address right now.',
      });
    } finally {
      setAddressSaving(false);
    }
  };

  const handleAddressDelete = async (addressId) => {
    setAddressSaving(true);
    setAddressMessage({ type: '', text: '' });

    try {
      const response = await api.delete(`/profile/addresses/${addressId}`);
      applyUpdatedUser(response.data?.user || activeProfile);
      if (editingAddressId === addressId) {
        resetAddressEditor();
      }
      setAddressMessage({
        type: 'success',
        text: response.data?.message || 'Address deleted successfully.',
      });
    } catch (error) {
      if (isSessionInvalidError(error)) {
        handleSessionInvalid('Your session expired while deleting the address. Please sign in again.');
        return;
      }
      setAddressMessage({
        type: 'danger',
        text: error.response?.data?.message || 'Unable to delete address right now.',
      });
    } finally {
      setAddressSaving(false);
    }
  };

  const handleAddressDefault = async (addressId) => {
    setAddressSaving(true);
    setAddressMessage({ type: '', text: '' });

    try {
      const response = await api.put(`/profile/addresses/${addressId}/default`);
      applyUpdatedUser(response.data?.user || activeProfile);
      setAddressMessage({
        type: 'success',
        text: response.data?.message || 'Default address updated successfully.',
      });
    } catch (error) {
      if (isSessionInvalidError(error)) {
        handleSessionInvalid('Your session expired while updating the default address. Please sign in again.');
        return;
      }
      setAddressMessage({
        type: 'danger',
        text: error.response?.data?.message || 'Unable to update the default address right now.',
      });
    } finally {
      setAddressSaving(false);
    }
  };

  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    setSearchParams(sectionId === 'overview' ? {} : { section: sectionId });
  };

  return (
    <div className="dashboard-bg">
      <div className="hero-banner-variant">
        <div className="container">
          <h1 className="dashboard-hero-title">
            My <em className="gold-text">Account</em>
          </h1>
        </div>
      </div>
      <div className="container dashboard-content-wrap">
        <div className="row g-4">
          {/* Sidebar */}
          <div className="col-lg-3">
            <div className="profile-sidebar">
              <div className="profile-avatar-wrap">
              <div className="profile-avatar">{getProfileInitials(activeProfile?.name)}</div>
              <p className="profile-name">{activeProfile?.name || 'Guest User'}</p>
              <p className="profile-email">{activeProfile?.email || 'Not signed in'}</p>
              </div>
              {DASHBOARD_NAV.map(n => (
                <button
                  key={n.id}
                  className={`profile-nav-link ${activeSection === n.id ? 'active' : ''}`}
                  onClick={() => handleSectionChange(n.id)}
                >
                  <i className={`fas ${n.icon}`} />
                  {n.label}
                  {n.id === 'wishlist' && wishlist.length > 0 && (
                    <span className="wishlist-badge">{wishlist.length}</span>
                  )}
                </button>
              ))}
              <button
                className="profile-nav-link sign-out-btn"
                onClick={() => {
                  logout();
                  navigate('/login');
                }}
              >
                <i className="fas fa-right-from-bracket" />Sign Out
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="col-lg-9">
            {!isAuthenticated ? (
              <div className="profile-card">
                <p className="profile-card-title"><i className="fas fa-user-lock" />Sign in required</p>
                <div className="empty-center-align">
                  <i className="fas fa-user-lock empty-heart-icon" />
                  <p className="empty-text">{profileError || 'Please sign in to manage your wishlist, addresses, and settings.'}</p>
                  <div className="co-auth-prompt__actions">
                    <Link to="/login" className="btn-gold">Sign In</Link>
                    <Link to="/register" className="btn-outline-navy">Create Account</Link>
                  </div>
                </div>
              </div>
            ) : (
              <>
            {activeSection === 'overview' && (
              <>
                <div className="row g-3 mb-4">
                  {[
                    { num: orderCards.length, label: 'Total Orders', icon: 'fa-bag-shopping' },
                    { num: formatCurrency(totalSpent), label: 'Total Spent', icon: 'fa-indian-rupee-sign' },
                    { num: wishlist.length, label: 'Wishlist Items', icon: 'fa-heart' },
                    { num: addresses.length, label: 'Saved Addresses', icon: 'fa-map-location-dot' }
                  ].map(s => (
                    <div key={s.label} className="col-6 col-md-3">
                      <div className="stat-card">
                        <i className={`fas ${s.icon} stat-icon`} />
                        <div className="stat-num">{s.num}</div>
                        <div className="stat-label">{s.label}</div>
                      </div>
                    </div>
                  ))}
                </div>
                {profileLoading && <div className="profile-card"><p className="order-meta-text mb-0">Loading your account details...</p></div>}
                {!profileLoading && profileError && <div className="profile-card"><p className="order-meta-text mb-0">{profileError}</p></div>}
                <div className="profile-card">
                  <p className="profile-card-title">
                    <i className="fas fa-clock-rotate-left" />Recent Orders
                  </p>
                  {ordersLoading && <p className="order-meta-text mb-0">Loading your orders...</p>}
                  {!ordersLoading && ordersError && <p className="order-meta-text mb-0">{ordersError}</p>}
                  {!ordersLoading && !ordersError && recentOrders.length === 0 && (
                    <p className="order-meta-text mb-0">No orders found yet.</p>
                  )}
                  {!ordersLoading && !ordersError && recentOrders.map(o => {
                    const s = STATUS_CFG[o.status] || STATUS_CFG.pending;
                    return (
                      <div key={o.id} className="order-item">
                        <OrderImage
                          src={o.image}
                          alt={o.title}
                          className="order-item-img-sm"
                        />
                        <div className="flex-fill-min-width">
                          <p className="order-product-name">{o.title}</p>
                          <p className="order-meta-text">{o.id} | {o.date}</p>
                          {o.trackingNumber && (
                            <p className="order-meta-text">
                              Tracking: <a href={o.trackingUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#d4a574' }}>
                                {o.trackingNumber}
                              </a>
                            </p>
                          )}
                        </div>
                        <span className={`order-status ${s.cls}`}>
                          <i className={`fas ${s.icon}`} style={{ marginRight: 4 }} />
                          {s.label}
                        </span>
                        <span className="order-amount-text">
                          {formatCurrency(o.amount)}
                        </span>
                      </div>
                    );
                  })}
                  {!ordersLoading && orderCards.length > 0 && (
                    <button
                      className="btn-outline-navy view-all-orders-btn"
                      onClick={() => handleSectionChange('orders')}
                    >
                      View All Orders <i className="fas fa-arrow-right" />
                    </button>
                  )}
                </div>
              </>
            )}

            {activeSection === 'orders' && (
              <div className="profile-card">
                <p className="profile-card-title"><i className="fas fa-bag-shopping" />My Orders</p>
                {ordersLoading && <p className="order-meta-text mb-0">Loading your orders...</p>}
                {!ordersLoading && ordersError && <p className="order-meta-text mb-0">{ordersError}</p>}
                {!ordersLoading && !ordersError && orderCards.length === 0 && (
                  <p className="order-meta-text mb-0">No orders found yet.</p>
                )}
                {!ordersLoading && !ordersError && orderCards.map(o => {
                  const s = STATUS_CFG[o.status] || STATUS_CFG.pending;
                  return (
                    <div key={o.id} className="order-item">
                      <OrderImage
                        src={o.image}
                        alt={o.title}
                        className="order-item-img-lg"
                      />
                      <div className="flex-fill-min-width">
                        <p className="order-product-title-lg">{o.title}</p>
                        <p className="order-meta-text-lg">Order ID: {o.id} | {o.date}</p>
                        {o.trackingNumber && (
                          <p className="order-meta-text-lg">
                            Tracking: <a href={o.trackingUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#d4a574' }}>
                              {o.trackingNumber}
                            </a>
                          </p>
                        )}
                        <span className={`order-status ${s.cls}`}>
                          <i className={`fas ${s.icon}`} style={{ marginRight: 4 }} />
                          {s.label}
                        </span>
                      </div>
                      <div className="order-right-action-wrap">
                        <p className="order-amount-text-lg">{formatCurrency(o.amount)}</p>
                        {o.status === 'delivered' && (
                          <button className="btn-outline-navy reorder-btn" onClick={() => navigate('/shop')}>
                            Reorder
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {activeSection === 'wishlist' && (
              <div className="profile-card">
                <p className="profile-card-title"><i className="fas fa-heart" />My Wishlist ({wishlist.length} items)</p>
                {wishlistMessage.text && (
                  <div className={`alert alert-${wishlistMessage.type} py-2`} role="alert">
                    {wishlistMessage.text}
                  </div>
                )}
                {wishlistLoading ? (
                  <p className="order-meta-text mb-0">Loading your wishlist...</p>
                ) : wishlistError ? (
                  <p className="order-meta-text mb-0">{wishlistError}</p>
                ) : wishlist.length === 0 ? (
                  <div className="empty-center-align">
                    <i className="far fa-heart empty-heart-icon" />
                    <p className="empty-text">Your wishlist is empty</p>
                    <Link to="/shop" className="btn-gold">Explore Products</Link>
                  </div>
                ) : (
                  wishlist.map(p => (
                    <div key={p.id} className="wishlist-item-row">
                      <img
                        src={p.images?.front}
                        alt={p.name}
                        className="wishlist-img"
                        onError={e => {
                          e.target.src = process.env.PUBLIC_URL + '/images/jewellery-img1.jpg';
                        }}
                      />
                      <div className="flex-fill">
                        <Link to={`/product/${p.id}`} className="wishlist-prod-link">{p.name}</Link>
                        <p className="wishlist-price">{formatCurrency(p.price)}</p>
                      </div>
                      <div className="wishlist-actions">
                        <button
                          className="btn-outline-navy wishlist-remove-btn"
                          onClick={() => handleWishlistRemove(p)}
                          disabled={wishlistLoading}
                        >
                          Remove
                        </button>
                        <Link to={`/product/${p.id}`} className="btn-gold wishlist-view-btn">View</Link>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeSection === 'addresses' && (
              <div className="profile-card">
                <p className="profile-card-title"><i className="fas fa-map-location-dot" />Saved Addresses</p>
                {addressMessage.text && (
                  <div className={`alert alert-${addressMessage.type} py-2`} role="alert">
                    {addressMessage.text}
                  </div>
                )}
                {profileLoading ? (
                  <p className="order-meta-text mb-0">Loading your addresses...</p>
                ) : addresses.length === 0 ? (
                  <div className="empty-center-align">
                    <i className="fas fa-map-location-dot empty-heart-icon" />
                    <p className="empty-text">No saved addresses yet.</p>
                  </div>
                ) : (
                  addresses.map((address) => (
                    <div key={address.id} className="address-card">
                      <div className="address-card-flex">
                        <div>
                          <p className="address-title">{address.label}</p>
                          <p className="address-name">{address.fullName}</p>
                          <p className="address-details">
                            {getAddressLines(address).map((line) => (
                              <React.Fragment key={line}>
                                {line}<br />
                              </React.Fragment>
                            ))}
                            {address.phone}
                          </p>
                        </div>
                        <div className="address-actions">
                          {address.isDefault && <span className="default-badge">Default</span>}
                          {!address.isDefault && (
                            <button
                              className="btn-outline-navy address-action-btn"
                              onClick={() => handleAddressDefault(address.id)}
                              disabled={addressSaving}
                            >
                              Make Default
                            </button>
                          )}
                          <button
                            className="btn-outline-navy address-action-btn"
                            onClick={() => {
                              setEditingAddressId(address.id);
                              setAddressForm(buildAddressForm(address));
                              setAddressEditorOpen(true);
                              setAddressMessage({ type: '', text: '' });
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn-outline-navy address-action-btn address-action-btn--danger"
                            onClick={() => handleAddressDelete(address.id)}
                            disabled={addressSaving}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <button
                  className="btn-outline-navy add-new-address-btn"
                  onClick={() => {
                    if (addressEditorOpen && !editingAddressId) {
                      resetAddressEditor();
                      setAddressMessage({ type: '', text: '' });
                      return;
                    }

                    setEditingAddressId('');
                    setAddressForm(buildAddressForm({
                      fullName: activeProfile?.name || '',
                      phone: activeProfile?.phone || '',
                    }));
                    setAddressEditorOpen(true);
                    setAddressMessage({ type: '', text: '' });
                  }}
                >
                  <i className={`fas ${addressEditorOpen ? 'fa-minus' : 'fa-plus'}`} /> {addressEditorOpen ? 'Close Address Form' : 'Add New Address'}
                </button>
                {addressEditorOpen && (
                  <form className="dashboard-form-grid" onSubmit={handleAddressSubmit}>
                    <div className="row g-3">
                      {[
                        { key: 'label', label: 'Label', col: 'col-md-4', placeholder: 'Home' },
                        { key: 'fullName', label: 'Full Name', col: 'col-md-4', placeholder: 'Recipient name' },
                        { key: 'phone', label: 'Phone', col: 'col-md-4', placeholder: '+91 98765 43210' },
                        { key: 'line1', label: 'Address Line 1', col: 'col-12', placeholder: 'House no, street, landmark' },
                        { key: 'line2', label: 'Address Line 2', col: 'col-12', placeholder: 'Area, apartment, optional' },
                        { key: 'city', label: 'City', col: 'col-md-4', placeholder: 'City' },
                        { key: 'state', label: 'State', col: 'col-md-4', placeholder: 'State' },
                        { key: 'postalCode', label: 'Postal Code', col: 'col-md-4', placeholder: '600040' },
                        { key: 'country', label: 'Country', col: 'col-md-4', placeholder: 'India' },
                      ].map((field) => (
                        <div key={field.key} className={field.col}>
                          <label className="settings-label">{field.label}</label>
                          <input
                            value={addressForm[field.key]}
                            className="settings-input"
                            onChange={(event) =>
                              setAddressForm((current) => ({ ...current, [field.key]: event.target.value }))
                            }
                            placeholder={field.placeholder}
                            required={['fullName', 'phone', 'line1', 'city', 'state', 'postalCode'].includes(field.key)}
                          />
                        </div>
                      ))}
                      <div className="col-12">
                        <label className="settings-toggle">
                          <input
                            type="checkbox"
                            checked={addressForm.isDefault}
                            onChange={(event) =>
                              setAddressForm((current) => ({ ...current, isDefault: event.target.checked }))
                            }
                          />
                          <span>Make this my default address</span>
                        </label>
                      </div>
                    </div>
                    <div className="dashboard-form-actions">
                      <button className="btn-gold save-settings-btn" type="submit" disabled={addressSaving}>
                        {addressSaving ? 'Saving...' : editingAddressId ? 'Update Address' : 'Save Address'}
                      </button>
                      <button type="button" className="btn-outline-navy save-settings-btn" onClick={resetAddressEditor}>
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}

            {activeSection === 'settings' && (
              <div className="profile-card">
                <p className="profile-card-title"><i className="fas fa-gear" />Account Settings</p>
                {settingsMessage.text && (
                  <div className={`alert alert-${settingsMessage.type} py-2`} role="alert">
                    {settingsMessage.text}
                  </div>
                )}
                <form onSubmit={handleSettingsSubmit}>
                  <div className="row g-3">
                    {[
                      { key: 'name', label: 'Full Name', type: 'text' },
                      { key: 'phone', label: 'Phone', type: 'tel' },
                      { key: 'email', label: 'Email', type: 'email' },
                    ].map((field) => (
                      <div key={field.key} className="col-md-6">
                        <label className="settings-label">{field.label}</label>
                        <input
                          type={field.type}
                          value={settingsForm[field.key]}
                          className="settings-input"
                          onChange={(event) =>
                            setSettingsForm((current) => ({ ...current, [field.key]: event.target.value }))
                          }
                          required
                        />
                      </div>
                    ))}
                    <div className="col-12">
                      <div className="settings-toggle-grid">
                        {[
                          { key: 'marketingEmails', label: 'Receive marketing emails' },
                          { key: 'orderUpdates', label: 'Receive order updates' },
                          { key: 'wishlistAlerts', label: 'Receive wishlist alerts' },
                        ].map((setting) => (
                          <label key={setting.key} className="settings-toggle">
                            <input
                              type="checkbox"
                              checked={settingsForm[setting.key]}
                              onChange={(event) =>
                                setSettingsForm((current) => ({
                                  ...current,
                                  [setting.key]: event.target.checked,
                                }))
                              }
                            />
                            <span>{setting.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                  <button className="btn-gold save-settings-btn" type="submit" disabled={settingsSaving}>
                    {settingsSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </form>
                <div className="settings-password-panel">
                  <p className="profile-card-title settings-password-title">
                    <i className="fas fa-lock" />Change Password
                  </p>
                  {passwordMessage.text && (
                    <div className={`alert alert-${passwordMessage.type} py-2`} role="alert">
                      {passwordMessage.text}
                    </div>
                  )}
                  <form onSubmit={handlePasswordSubmit}>
                    <div className="row g-3">
                      {[
                        { key: 'currentPassword', label: 'Current Password' },
                        { key: 'newPassword', label: 'New Password' },
                        { key: 'confirmPassword', label: 'Confirm Password' },
                      ].map((field) => (
                        <div key={field.key} className="col-md-4">
                          <label className="settings-label">{field.label}</label>
                          <input
                            type="password"
                            value={passwordForm[field.key]}
                            className="settings-input"
                            onChange={(event) =>
                              setPasswordForm((current) => ({ ...current, [field.key]: event.target.value }))
                            }
                            required
                          />
                        </div>
                      ))}
                    </div>
                    <button className="btn-outline-navy save-settings-btn" type="submit" disabled={passwordSaving}>
                      {passwordSaving ? 'Updating...' : 'Update Password'}
                    </button>
                  </form>
                </div>
              </div>
            )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* POLICY SHELL */
function PolicyShell({ title, children }) {
  return (
    <div className="page-wrapper">
      <div className="hero-banner">
        <div className="container">
          <h1 className="policy-hero-title" dangerouslySetInnerHTML={{ __html: title }} />
        </div>
      </div>
      <div className="container policy-content-wrap">
        <div className="policy-card-inner">
          {children}
        </div>
      </div>
    </div>
  );
}

const POLICY_PAGE_CONFIG = {
  privacy: { title: 'Privacy <em class="gold-text">Policy</em>' },
  refund: { title: 'Refund <em class="gold-text">Policy</em>' },
  terms: { title: 'Terms of <em class="gold-text">Service</em>' },
};

const formatPolicyDate = (value) => {
  if (!value) return 'Not updated yet';

  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Not updated yet'
    : date.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
};

function PolicyPage({ policyType }) {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const { title } = POLICY_PAGE_CONFIG[policyType];

  useEffect(() => {
    let active = true;

    const loadPolicy = async () => {
      try {
        const response = await api.get('/policies');
        if (active) {
          setPolicy(response.data?.data?.[policyType] || null);
        }
      } catch (error) {
        console.error(`Unable to load ${policyType} policy:`, error);
      } finally {
        if (active) setLoading(false);
      }
    };

    loadPolicy();
    return () => {
      active = false;
    };
  }, [policyType]);

  return (
    <PolicyShell title={title}>
      {loading ? (
        <p>Loading policy...</p>
      ) : policy?.content ? (
        <div className="policy-content-text">{policy.content}</div>
      ) : (
        <p>This policy is not available yet.</p>
      )}
    </PolicyShell>
  );
}

export function PrivacyPolicyPage() {
  return <PolicyPage policyType="privacy" />;
}

export function RefundPolicyPage() {
  return <PolicyPage policyType="refund" />;
}

export function TermsPage() {
  return <PolicyPage policyType="terms" />;
}